import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { AnimatePresence, motion } from "framer-motion";
import {
  SpinnerGap, FloppyDisk, Palette, Trash, Plus, Check, PencilSimple,
} from "@phosphor-icons/react";

export function BrandPaletteSection() {
  const { api } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Brand palette (default colors)
  const [bgColor, setBgColor] = useState("#FFFFFF");
  const [primaryColor, setPrimaryColor] = useState("#007bff");
  const [textColor, setTextColor] = useState("#1a1a2e");
  const [accentColor, setAccentColor] = useState("#28a745");
  const [useAccent, setUseAccent] = useState(false);

  // Brand themes (up to 10)
  const [themes, setThemes] = useState([]);
  const [addingTheme, setAddingTheme] = useState(false);
  const [newThemeName, setNewThemeName] = useState("");

  const loadPalette = useCallback(async () => {
    try {
      const resp = await api.get("/profile/brand-palette");
      const palette = resp.data.brand_palette;
      if (palette) {
        setBgColor(palette.background_color || "#FFFFFF");
        setPrimaryColor(palette.primary_color || "#007bff");
        setTextColor(palette.text_color || "#1a1a2e");
        setAccentColor(palette.accent_color || "#28a745");
        setUseAccent(palette.use_accent || false);
      }
      setThemes(resp.data.brand_themes || []);
    } catch {} finally { setLoading(false); }
  }, [api]);

  useEffect(() => { loadPalette(); }, [loadPalette]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post("/profile/brand-palette", {
        brand_palette: {
          background_color: bgColor,
          primary_color: primaryColor,
          text_color: textColor,
          accent_color: accentColor,
          use_accent: useAccent,
        },
        brand_themes: themes,
      });
      toast.success("Brand palette saved!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save palette");
    } finally { setSaving(false); }
  };

  const handleAddTheme = () => {
    if (!newThemeName.trim()) { toast.error("Enter a theme name"); return; }
    if (themes.length >= 10) { toast.error("Maximum 10 themes allowed"); return; }
    setThemes(prev => [...prev, {
      name: newThemeName.trim(),
      background_color: bgColor,
      primary_color: primaryColor,
      text_color: textColor,
      accent_color: accentColor,
      use_accent: useAccent,
    }]);
    setNewThemeName("");
    setAddingTheme(false);
    toast.success(`Theme "${newThemeName.trim()}" added — save to persist`);
  };

  const handleDeleteTheme = (idx) => {
    setThemes(prev => prev.filter((_, i) => i !== idx));
    toast.success("Theme removed — save to persist");
  };

  const handleLoadTheme = (theme) => {
    setBgColor(theme.background_color);
    setPrimaryColor(theme.primary_color);
    setTextColor(theme.text_color);
    setAccentColor(theme.accent_color);
    setUseAccent(theme.use_accent);
    toast.success(`Loaded "${theme.name}" into default palette`);
  };

  if (loading) {
    return (
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-6">
        <div className="flex justify-center py-8"><SpinnerGap size={24} className="animate-spin text-[#FF4400]" /></div>
      </div>
    );
  }

  return (
    <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-6" data-testid="brand-palette-section">
      <div className="flex items-center gap-2.5 mb-1">
        <Palette size={20} weight="duotone" className="text-[#FF4400]" />
        <h2 className="font-['Cormorant_Garamond'] text-lg font-semibold text-[#FF4400]">Brand Palette</h2>
      </div>
      <p className="text-sm text-[var(--text-muted)] mb-6">
        Set your brand colors here. These become the default palette whenever you create visuals in the Graphic Generator.
      </p>

      {/* Default palette colors */}
      <div className="space-y-4 mb-6">
        <p className="font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)]">Default Palette</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <PaletteColorPicker label="Background" value={bgColor} onChange={setBgColor} testId="brand-color-bg" />
          <PaletteColorPicker label="Primary" value={primaryColor} onChange={setPrimaryColor} testId="brand-color-primary" />
          <PaletteColorPicker label="Text" value={textColor} onChange={setTextColor} testId="brand-color-text" />
        </div>

        {/* Accent toggle */}
        <div className="flex items-center gap-3 bg-[var(--bg-base)] border border-[var(--border)] rounded-xl p-3">
          <Switch checked={useAccent} onCheckedChange={setUseAccent} data-testid="brand-accent-toggle" />
          <span className="text-xs font-medium text-[var(--text-secondary)]">
            {useAccent ? "Accent color enabled" : "Add accent color"}
          </span>
          {useAccent && (
            <div className="ml-auto flex items-center gap-2">
              <input type="color" value={accentColor} onChange={e => setAccentColor(e.target.value)}
                className="w-7 h-7 rounded-lg border-0 cursor-pointer bg-transparent shrink-0 [&::-webkit-color-swatch]:rounded-md [&::-webkit-color-swatch-wrapper]:p-0"
                data-testid="brand-color-accent" />
              <input type="text" value={accentColor}
                onChange={e => { if (/^#[0-9a-fA-F]{0,6}$/.test(e.target.value)) setAccentColor(e.target.value); }}
                className="bg-transparent text-[11px] font-mono text-[var(--text-secondary)] w-16 outline-none" />
            </div>
          )}
        </div>

        {/* Preview strip */}
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-[var(--text-muted)] font-medium shrink-0">Preview:</span>
          <div className="flex gap-1 flex-1">
            {[bgColor, primaryColor, textColor, ...(useAccent ? [accentColor] : [])].map((c, i) => (
              <div key={`preview-${c}-${i}`} className="flex-1 h-6 rounded-md border border-white/10" style={{ backgroundColor: c }} />
            ))}
          </div>
        </div>
      </div>

      {/* Brand Themes */}
      <div className="border-t border-[var(--border)] pt-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <p className="font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)]">
            Brand Themes <span className="text-[var(--text-muted)]/60">({themes.length}/10)</span>
          </p>
          {themes.length < 10 && (
            <button
              onClick={() => setAddingTheme(!addingTheme)}
              className="flex items-center gap-1 text-[10px] font-semibold text-[#FF4400] hover:text-[#FF6633] transition-colors"
              data-testid="brand-add-theme-btn"
            >
              <Plus size={12} weight="bold" /> Save Current as Theme
            </button>
          )}
        </div>

        {/* Add theme form */}
        <AnimatePresence>
          {addingTheme && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden mb-3"
            >
              <div className="flex items-center gap-2 bg-[var(--bg-base)] border border-[var(--border)] rounded-xl p-2.5">
                <Input value={newThemeName} onChange={e => setNewThemeName(e.target.value.slice(0, 50))}
                  placeholder="Theme name..."
                  className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm h-8 flex-1"
                  onKeyDown={e => { if (e.key === "Enter") handleAddTheme(); }}
                  data-testid="brand-theme-name-input" />
                <Button onClick={handleAddTheme} disabled={!newThemeName.trim()}
                  className="h-8 px-3 bg-[#FF4400] hover:bg-[#FF6633] text-white text-xs rounded-lg"
                  data-testid="brand-confirm-add-theme">
                  Add
                </Button>
                <div className="flex gap-0.5 ml-1">
                  {[bgColor, primaryColor, textColor, ...(useAccent ? [accentColor] : [])].map((c, i) => (
                    <div key={`new-${c}-${i}`} className="w-4 h-4 rounded-sm border border-white/10" style={{ backgroundColor: c }} />
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Theme list */}
        {themes.length > 0 ? (
          <div className="space-y-1.5">
            {themes.map((theme, idx) => (
              <div key={`brand-theme-${theme.name}-${idx}`}
                className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-[var(--bg-base)] border border-[var(--border)] hover:border-[var(--border-strong)] transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="flex gap-0.5">
                    <div className="w-5 h-5 rounded-sm border border-white/10" style={{ backgroundColor: theme.background_color }} />
                    <div className="w-5 h-5 rounded-sm border border-white/10" style={{ backgroundColor: theme.primary_color }} />
                    <div className="w-5 h-5 rounded-sm border border-white/10" style={{ backgroundColor: theme.text_color }} />
                    {theme.use_accent && <div className="w-5 h-5 rounded-sm border border-white/10" style={{ backgroundColor: theme.accent_color }} />}
                  </div>
                  <span className="text-sm font-medium text-[var(--text-primary)]">{theme.name}</span>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => handleLoadTheme(theme)}
                    className="p-1.5 rounded-md text-[var(--text-muted)] hover:text-[#FF4400] hover:bg-[#FF4400]/10 transition-colors"
                    title="Load into default palette"
                    data-testid={`brand-load-theme-${idx}`}
                  >
                    <PencilSimple size={14} />
                  </button>
                  <button onClick={() => handleDeleteTheme(idx)}
                    className="p-1.5 rounded-md text-[var(--text-muted)] hover:text-red-400 hover:bg-red-500/10 transition-colors"
                    title="Delete theme"
                    data-testid={`brand-delete-theme-${idx}`}
                  >
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-[var(--text-muted)] text-center py-4">
            No themes yet. Set your colors above and click "Save Current as Theme" to create one.
          </p>
        )}
      </div>

      {/* Save button */}
      <div className="flex justify-end pt-2 border-t border-[var(--border)]">
        <Button onClick={handleSave} disabled={saving} className="bg-[#FF4400] hover:bg-[#FF6633] text-white gap-2" data-testid="save-brand-palette-btn">
          {saving ? <SpinnerGap size={16} className="animate-spin" /> : <FloppyDisk size={16} weight="bold" />}
          Save Brand Palette
        </Button>
      </div>
    </div>
  );
}


function PaletteColorPicker({ label, value, onChange, testId }) {
  return (
    <div className="bg-[var(--bg-base)] border border-[var(--border)] rounded-xl p-3 flex items-center gap-2.5">
      <input type="color" value={value} onChange={e => onChange(e.target.value)}
        className="w-9 h-9 rounded-lg border-0 cursor-pointer bg-transparent shrink-0 [&::-webkit-color-swatch]:rounded-md [&::-webkit-color-swatch-wrapper]:p-0"
        data-testid={testId} />
      <div className="min-w-0">
        <span className="block text-[9px] text-[var(--text-muted)] uppercase tracking-wider font-medium">{label}</span>
        <input type="text" value={value}
          onChange={e => { if (/^#[0-9a-fA-F]{0,6}$/.test(e.target.value)) onChange(e.target.value); }}
          className="bg-transparent text-xs font-mono text-[var(--text-secondary)] w-full outline-none"
          data-testid={`${testId}-hex`} />
      </div>
    </div>
  );
}
